import { useState, useEffect, useRef } from 'react'
import { Send, Loader2, CheckCircle, AlertCircle } from 'lucide-react'

interface ContactProps {
  onNavigate: (page: string) => void
}

interface FormData {
  name: string
  email: string
  company: string
  organizationType: string
  service: string
  message: string
}

const initialFormData: FormData = {
  name: '',
  email: '',
  company: '',
  organizationType: '',
  service: '',
  message: '',
}

export const Contact: React.FC<ContactProps> = ({ onNavigate }) => {
  const [formData, setFormData] = useState<FormData>(initialFormData)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [errorMessage, setErrorMessage] = useState('')
  const [isVisible, setIsVisible] = useState(false)
  const sectionRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
        }
      },
      { threshold: 0.2 }
    )

    if (sectionRef.current) {
      observer.observe(sectionRef.current)
    }

    return () => observer.disconnect()
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus('idle')
    setErrorMessage('')

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      const result = await response.json()

      if (result.success) {
        setSubmitStatus('success')
        setFormData(initialFormData)
        // Navigate to thank you page after a brief delay
        setTimeout(() => {
          onNavigate('thank-you')
        }, 1500)
      } else {
        setSubmitStatus('error')
        setErrorMessage(result.error || 'Submission failed. Please try again.')
      }
    } catch (error) {
      console.error('Submission error:', error)
      setSubmitStatus('error')
      setErrorMessage('An error occurred. Please check your connection and try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const organizationTypes = [
    'Higher Education',
    'K-12',
    'Corporate Training',
    'Government',
    'Non-Profit',
    'Other',
  ]

  const services = [
    'Strategic EdTech Consulting',
    'Digital Transformation',
    'AI-Enhanced Learning Design',
    'Immersive Learning Solutions',
    'Data Analysis & Reporting',
    'Accessibility & Compliance',
    'Other',
  ]

  return (
    <section
      id="contact"
      ref={sectionRef}
      className="section scroll-mt-20"
      aria-labelledby="contact-heading"
    >
      <div className="container-narrow">
        {/* Section Header */}
        <div
          className={`text-center mb-12 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2
            id="contact-heading"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6"
          >
            Let's Connect
          </h2>
          <p className="text-lg text-slate-600">
            Ready to transform your learning experience? Tell us about your project.
          </p>
        </div>

        {/* Contact Form */}
        <form
          onSubmit={handleSubmit}
          className={`bg-white border border-slate-200 p-6 sm:p-8 lg:p-10 rounded-2xl shadow-lg transition-all duration-700 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          noValidate
        >
          {/* Honeypot field for spam protection */}
          <input
            type="text"
            name="honeypot"
            style={{ display: 'none' }}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
          />

          {/* Success Message */}
          {submitStatus === 'success' && (
            <div
              className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3 text-green-800"
              role="alert"
            >
              <CheckCircle size={20} aria-hidden="true" />
              <span className="font-medium">Success! Redirecting you now...</span>
            </div>
          )}

          {/* Error Message */}
          {submitStatus === 'error' && (
            <div
              className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3 text-red-800"
              role="alert"
            >
              <AlertCircle size={20} className="mt-0.5" aria-hidden="true" />
              <div>
                <p className="font-medium">Submission failed</p>
                <p className="text-sm">{errorMessage}</p>
              </div>
            </div>
          )}

          {/* Name & Email Row */}
          <div className="grid sm:grid-cols-2 gap-6 mb-6">
            <div className="space-y-2">
              <label
                htmlFor="name"
                className="block text-sm font-semibold text-slate-700"
              >
                Full Name <span className="text-red-500" aria-hidden="true">*</span>
                <span className="sr-only">(required)</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                disabled={isSubmitting}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="John Doe"
                aria-required="true"
                autoComplete="name"
              />
            </div>
            <div className="space-y-2">
              <label
                htmlFor="email"
                className="block text-sm font-semibold text-slate-700"
              >
                Email Address <span className="text-red-500" aria-hidden="true">*</span>
                <span className="sr-only">(required)</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                disabled={isSubmitting}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="john@example.com"
                aria-required="true"
                autoComplete="email"
              />
            </div>
          </div>

          {/* Company & Organization Type Row */}
          <div className="grid sm:grid-cols-2 gap-6 mb-6">
            <div className="space-y-2">
              <label
                htmlFor="company"
                className="block text-sm font-semibold text-slate-700"
              >
                Company/Institution
              </label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                disabled={isSubmitting}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Your organization"
                autoComplete="organization"
              />
            </div>
            <div className="space-y-2">
              <label
                htmlFor="organizationType"
                className="block text-sm font-semibold text-slate-700"
              >
                Organization Type
              </label>
              <select
                id="organizationType"
                name="organizationType"
                value={formData.organizationType}
                onChange={handleChange}
                disabled={isSubmitting}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed appearance-none bg-white"
              >
                <option value="">Select type...</option>
                {organizationTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Service Interest */}
          <div className="space-y-2 mb-6">
            <label
              htmlFor="service"
              className="block text-sm font-semibold text-slate-700"
            >
              Service Interested In
            </label>
            <select
              id="service"
              name="service"
              value={formData.service}
              onChange={handleChange}
              disabled={isSubmitting}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed appearance-none bg-white"
            >
              <option value="">Select a service...</option>
              {services.map((service) => (
                <option key={service} value={service}>
                  {service}
                </option>
              ))}
            </select>
          </div>

          {/* Message */}
          <div className="space-y-2 mb-8">
            <label
              htmlFor="message"
              className="block text-sm font-semibold text-slate-700"
            >
              Message <span className="text-red-500" aria-hidden="true">*</span>
              <span className="sr-only">(required)</span>
            </label>
            <textarea
              id="message"
              name="message"
              required
              rows={4}
              value={formData.message}
              onChange={handleChange}
              disabled={isSubmitting}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed resize-none"
              placeholder="Tell us about your project or challenge..."
              aria-required="true"
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting || submitStatus === 'success'}
            className="w-full btn-primary text-lg py-4 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="animate-spin" size={20} aria-hidden="true" />
                <span>Sending...</span>
              </>
            ) : submitStatus === 'success' ? (
              <>
                <CheckCircle size={20} aria-hidden="true" />
                <span>Sent Successfully!</span>
              </>
            ) : (
              <>
                <Send size={20} aria-hidden="true" />
                <span>Send Message</span>
              </>
            )}
          </button>
        </form>
      </div>
    </section>
  )
}
